import React from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Text } from '@/components/ui/typography';

const menuItems = [
  { icon: "PersonalInfo", text: "Personal Info", key: "Personal Info" },
  { icon: "Summary", text: "Summary", key: "Summary" },
  { icon: "WorkExperience", text: "Work Experience", key: "Work Experience" },
  { icon: "Education", text: "Education", key: "Education" },
  { icon: "Language", text: "Language", key: "Language" },
  { icon: "Certification", text: "Certification", key: "Certification" },
  { icon: "Project", text: "Project", key: "Project" },
  { icon: "Skills", text: "Skills", key: "Skills" },
  { icon: "Achievements", text: "Achievements", key: "Achievements" }
];

function HorizontalStepper({ activeSection, completedSections = [], onSectionClick }) {
  const getStepStatus = (stepKey) => {
    if (completedSections.includes(stepKey)) return 'completed';
    if (activeSection === stepKey) return 'active';
    return 'pending';
  };

  const calculateProgress = () => {
    if (completedSections.length === 0) return 0;
    return Math.round((completedSections.length / menuItems.length) * 100);
  };

  return (
    <div className="w-full bg-white border-b border-border sticky top-0 z-10 shadow-sm">
      {/* Progress Bar */}
      <div className="px-6 py-3 bg-muted/30">
        <div className="flex items-center justify-between mb-2">
          <Text size="sm" variant="muted" className="font-medium">
            Resume Progress
          </Text>
          <Text size="sm" variant="muted" className="font-semibold">
            {calculateProgress()}%
          </Text>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${calculateProgress()}%` }}
          />
        </div>
      </div>

      {/* Horizontal Stepper */}
      <div className="px-6 py-4 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          {menuItems.map((item, index) => {
            const status = getStepStatus(item.key);
            const isCompleted = status === 'completed';
            const isActive = status === 'active';
            const isPending = status === 'pending';

            return (
              <React.Fragment key={item.key}>
                {/* Step Indicator */}
                <button
                  onClick={() => onSectionClick(item.key)}
                  className={cn(
                    "flex flex-col items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 min-w-[100px]",
                    "hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                    isActive && "bg-primary/10",
                    isPending && "opacity-60"
                  )}
                >
                  {/* Step Circle */}
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300",
                      isCompleted && "bg-primary text-primary-foreground",
                      isActive && "bg-primary text-primary-foreground ring-4 ring-primary/20",
                      isPending && "bg-muted text-muted-foreground"
                    )}
                  >
                    {isCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  
                  {/* Step Label */}
                  <Text
                    size="xs"
                    className={cn(
                      "text-center font-medium whitespace-nowrap",
                      isActive && "text-primary font-semibold",
                      isCompleted && "text-foreground",
                      isPending && "text-muted-foreground"
                    )}
                  >
                    {item.text}
                  </Text>
                </button>

                {/* Connector Arrow */}
                {index < menuItems.length - 1 && (
                  <div className="flex items-center px-1">
                    <ChevronRight
                      className={cn(
                        "w-5 h-5 transition-colors",
                        isCompleted ? "text-primary" : "text-muted-foreground"
                      )}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default HorizontalStepper;
